import React from "react";
import { classMgmtService } from "services/admin/classMgmtService";
import { useLanguage } from "context/LanguageContext";
import type {
  ClassEnrollment,
  SessionParticipantOverlapItem,
  UserListItem,
} from "types/domain";

export type ParticipantPickerType = "teacher" | "trainer";

export type OverlapInfo = {
  userName: string;
  sessionNames: string[];
};

type UseSessionParticipantOverlapParams = {
  currentDateWindow: { from: string; to: string } | null;
  isEdit: boolean;
  sessionId?: string;
  students: UserListItem[];
  managers: UserListItem[];
  trainers: UserListItem[];
  enrollments: ClassEnrollment[];
  sessionTeacherIds: string[];
  sessionTrainerIds: string[];
  sessionStudentIds: string[];
  setSessionTrainerIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSessionStudentIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export const useSessionParticipantOverlap = ({
  currentDateWindow,
  isEdit,
  sessionId,
  students,
  managers,
  trainers,
  enrollments,
  sessionTeacherIds,
  sessionTrainerIds,
  sessionStudentIds,
  setSessionTrainerIds,
  setSessionStudentIds,
}: UseSessionParticipantOverlapParams) => {
  const { t } = useLanguage();
  const [loadingOverlapCheck, setLoadingOverlapCheck] = React.useState(false);
  const [managerOverlapMap, setManagerOverlapMap] = React.useState<
    Map<string, OverlapInfo>
  >(new Map());
  const [studentOverlapMap, setStudentOverlapMap] = React.useState<
    Map<string, OverlapInfo>
  >(new Map());
  const [trainerOverlapMap, setTrainerOverlapMap] = React.useState<
    Map<string, OverlapInfo>
  >(new Map());
  const [participantPicker, setParticipantPicker] =
    React.useState<ParticipantPickerType | null>(null);
  const [teacherSearch, setTeacherSearch] = React.useState("");
  const [trainerSearch, setTrainerSearch] = React.useState("");
  const lastOverlapWindowKeyRef = React.useRef<string | null>(null);
  const shouldNotifyAutoRemovalRef = React.useRef(false);
  const suppressAutoRemovalAlertRef = React.useRef(false);

  const buildOverlapMap = React.useCallback(
    (items: SessionParticipantOverlapItem[], userIds: string[]) => {
      const targetIds = new Set(userIds);
      const map = new Map<string, OverlapInfo>();

      items.forEach((item) => {
        if (!targetIds.has(item.user_id)) return;
        const existing = map.get(item.user_id);
        const fallbackUser =
          students.find((user) => user.id === item.user_id) ??
          managers.find((user) => user.id === item.user_id) ??
          trainers.find((user) => user.id === item.user_id);
        const userName = fallbackUser?.full_name ?? item.user_id;
        const sessionName =
          item.session_name || t("sessionEditor.unknownSessionName");

        if (existing) {
          if (!existing.sessionNames.includes(sessionName)) {
            existing.sessionNames.push(sessionName);
          }
          return;
        }

        map.set(item.user_id, {
          userName,
          sessionNames: [sessionName],
        });
      });

      return map;
    },
    [students, managers, trainers, t]
  );

  const checkOverlapsByIds = React.useCallback(
    async (userIds: string[]) => {
      if (!currentDateWindow || userIds.length === 0) {
        return new Map<string, OverlapInfo>();
      }

      const response = await classMgmtService.checkSessionParticipantOverlaps({
        user_ids: userIds,
        exclude_session_id: isEdit && sessionId ? sessionId : undefined,
        from_datetime: currentDateWindow.from,
        to_datetime: currentDateWindow.to,
      });

      return buildOverlapMap(response, userIds);
    },
    [buildOverlapMap, currentDateWindow, isEdit, sessionId]
  );

  const enrolledStudents = React.useMemo(() => {
    const map = new Map<string, UserListItem>();
    enrollments.forEach((enrollment) => {
      if (enrollment.remaining_sessions === 0) return;
      const fromEnrollment = enrollment.student;
      const fallback = students.find(
        (student) => student.id === enrollment.student_id
      );
      const user = fromEnrollment ?? fallback;
      if (user) map.set(user.id, user);
    });
    return Array.from(map.values());
  }, [enrollments, students]);

  const invalidRoleOverlapIds = React.useMemo(() => {
    const teacherSet = new Set(sessionTeacherIds);
    return sessionTrainerIds.filter((trainerId) => teacherSet.has(trainerId));
  }, [sessionTeacherIds, sessionTrainerIds]);

  const selectedScheduleConflictUsers = React.useMemo(() => {
    const ids = new Set<string>([...sessionTrainerIds, ...sessionStudentIds]);
    return Array.from(ids)
      .map((userId) => {
        const info =
          trainerOverlapMap.get(userId) ?? studentOverlapMap.get(userId);
        return { userId, info };
      })
      .filter((item): item is { userId: string; info: OverlapInfo } =>
        Boolean(item.info)
      );
  }, [
    sessionTrainerIds,
    sessionStudentIds,
    trainerOverlapMap,
    studentOverlapMap,
  ]);

  const selectedManagerConflictUsers = React.useMemo(() => {
    return sessionTeacherIds
      .map((userId) => ({ userId, info: managerOverlapMap.get(userId) }))
      .filter((item): item is { userId: string; info: OverlapInfo } =>
        Boolean(item.info)
      );
  }, [managerOverlapMap, sessionTeacherIds]);

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!currentDateWindow) {
        shouldNotifyAutoRemovalRef.current = false;
        setManagerOverlapMap(new Map());
        setTrainerOverlapMap(new Map());
        setStudentOverlapMap(new Map());
        setLoadingOverlapCheck(false);
        return;
      }

      const currentWindowKey = `${currentDateWindow.from}|${currentDateWindow.to}`;
      shouldNotifyAutoRemovalRef.current =
        lastOverlapWindowKeyRef.current !== null &&
        lastOverlapWindowKeyRef.current !== currentWindowKey;
      lastOverlapWindowKeyRef.current = currentWindowKey;

      const managerIds = managers.map((manager) => manager.id);
      const trainerIds = trainers.map((trainer) => trainer.id);
      const studentIds = enrolledStudents.map((student) => student.id);

      if (
        managerIds.length === 0 &&
        trainerIds.length === 0 &&
        studentIds.length === 0
      ) {
        setManagerOverlapMap(new Map());
        setTrainerOverlapMap(new Map());
        setStudentOverlapMap(new Map());
        setLoadingOverlapCheck(false);
        return;
      }

      setLoadingOverlapCheck(true);
      try {
        const [managerMap, trainerMap, studentMap] = await Promise.all([
          checkOverlapsByIds(managerIds),
          checkOverlapsByIds(trainerIds),
          checkOverlapsByIds(studentIds),
        ]);

        if (!cancelled) {
          setManagerOverlapMap(managerMap);
          setTrainerOverlapMap(trainerMap);
          setStudentOverlapMap(studentMap);
        }
      } catch {
        if (!cancelled) {
          setManagerOverlapMap(new Map());
          setTrainerOverlapMap(new Map());
          setStudentOverlapMap(new Map());
        }
      } finally {
        if (!cancelled) {
          setLoadingOverlapCheck(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    checkOverlapsByIds,
    currentDateWindow,
    enrolledStudents,
    managers,
    trainers,
  ]);

  React.useEffect(() => {
    const removedTrainerIds = sessionTrainerIds.filter((userId) =>
      trainerOverlapMap.has(userId)
    );
    const removedStudentIds = sessionStudentIds.filter((userId) =>
      studentOverlapMap.has(userId)
    );

    if (removedTrainerIds.length === 0 && removedStudentIds.length === 0) {
      if (shouldNotifyAutoRemovalRef.current) {
        shouldNotifyAutoRemovalRef.current = false;
      }
      return;
    }

    if (removedTrainerIds.length > 0) {
      setSessionTrainerIds((prev) =>
        prev.filter((userId) => !trainerOverlapMap.has(userId))
      );
    }

    if (removedStudentIds.length > 0) {
      setSessionStudentIds((prev) =>
        prev.filter((userId) => !studentOverlapMap.has(userId))
      );
    }

    if (
      suppressAutoRemovalAlertRef.current ||
      !shouldNotifyAutoRemovalRef.current
    ) {
      shouldNotifyAutoRemovalRef.current = false;
      return;
    }

    const trainerMessages = removedTrainerIds
      .map((userId) => trainerOverlapMap.get(userId))
      .filter((info): info is OverlapInfo => Boolean(info))
      .map((info) => `${info.userName}: ${info.sessionNames.join(", ")}`);

    const studentMessages = removedStudentIds
      .map((userId) => studentOverlapMap.get(userId))
      .filter((info): info is OverlapInfo => Boolean(info))
      .map((info) => `${info.userName}: ${info.sessionNames.join(", ")}`);

    const sections: string[] = [];
    if (trainerMessages.length > 0) {
      sections.push(
        t("sessionEditor.autoRemovedTrainers", {
          list: trainerMessages.join("\n- "),
        })
      );
    }
    if (studentMessages.length > 0) {
      sections.push(
        t("sessionEditor.autoRemovedStudents", {
          list: studentMessages.join("\n- "),
        })
      );
    }

    if (sections.length > 0) {
      alert(
        t("sessionEditor.autoRemovedAlert", { sections: sections.join("\n\n") })
      );
    }

    shouldNotifyAutoRemovalRef.current = false;
  }, [
    sessionTrainerIds,
    sessionStudentIds,
    setSessionStudentIds,
    setSessionTrainerIds,
    studentOverlapMap,
    trainerOverlapMap,
    t,
  ]);

  const filteredManagerOptions = React.useMemo(() => {
    const keyword = teacherSearch.trim().toLowerCase();
    if (!keyword) return managers;
    return managers.filter((manager) =>
      manager.full_name.toLowerCase().includes(keyword)
    );
  }, [teacherSearch, managers]);

  const filteredTrainerOptions = React.useMemo(() => {
    const keyword = trainerSearch.trim().toLowerCase();
    if (!keyword) return trainers;
    return trainers.filter((trainer) =>
      trainer.full_name.toLowerCase().includes(keyword)
    );
  }, [trainerSearch, trainers]);

  const handleOpenParticipantPicker = React.useCallback(
    (type: ParticipantPickerType) => {
      setParticipantPicker(type);
    },
    []
  );

  const closeParticipantPicker = React.useCallback(() => {
    setParticipantPicker(null);
  }, []);

  const recheckSelectedParticipantsBeforeSubmit =
    React.useCallback(async () => {
      try {
        suppressAutoRemovalAlertRef.current = true;

        if (!currentDateWindow) {
          return {
            trainerMap: new Map<string, OverlapInfo>(),
            studentMap: new Map<string, OverlapInfo>(),
          };
        }

        const [trainerMap, studentMap] = await Promise.all([
          checkOverlapsByIds(sessionTrainerIds),
          checkOverlapsByIds(sessionStudentIds),
        ]);

        setTrainerOverlapMap(trainerMap);
        setStudentOverlapMap(studentMap);

        return { trainerMap, studentMap };
      } finally {
        suppressAutoRemovalAlertRef.current = false;
      }
    }, [
      checkOverlapsByIds,
      currentDateWindow,
      sessionStudentIds,
      sessionTrainerIds,
    ]);

  return {
    enrolledStudents,
    loadingOverlapCheck,
    managerOverlapMap,
    studentOverlapMap,
    trainerOverlapMap,
    participantPicker,
    teacherSearch,
    trainerSearch,
    setTeacherSearch,
    setTrainerSearch,
    filteredManagerOptions,
    filteredTrainerOptions,
    invalidRoleOverlapIds,
    selectedScheduleConflictUsers,
    selectedManagerConflictUsers,
    handleOpenParticipantPicker,
    closeParticipantPicker,
    recheckSelectedParticipantsBeforeSubmit,
  };
};
